import { useState, useEffect, useCallback } from "react";
import {
  obtenerProgramacionesPorModulo,
  crearProgramacion as apiCrearProgramacion,
  actualizarProgramacion as apiActualizarProgramacion,
  eliminarProgramacion as apiEliminarProgramacion,
} from "../../services/ProgramacionService";

/**
 * Hook para manejar programaciones de un módulo
 * @param {number|string} moduloId
 */
export function useProgramaciones(moduloId) {
  const [programaciones, setProgramaciones] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [ultimoId, setUltimoId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState("");

  // Función para recargar datos desde la API
  const fetchData = useCallback(async () => {
    if (!moduloId) return;
    setLoading(true);
    try {
      const resp = await obtenerProgramacionesPorModulo(moduloId);
      const datos = resp.datos || [];
      setProgramaciones(datos);
      const ids = datos.map((p) => p.programacionId);
      setUltimoId(ids.length ? Math.max(...ids) : 0);
      //setMensaje(resp.mensaje || "");
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [moduloId]);

  // Carga inicial y re-fetch cuando cambia módulo
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Crear
  async function agregarProgramacion(nueva) {
    try {
      const resp = await apiCrearProgramacion(nueva);
      setMensaje(resp.mensaje || "");
      await fetchData();
      setHistorial((h) => [
        ...h,
        {
          id: Date.now(),
          tipo: "Alta",
          fecha: new Date().toLocaleString(),
          programacionId: nueva.programacionId,
          sectores: (nueva.configuracion.parameters.valves || [])
            .map((v) => String(v.valvulaId))
            .join(", "),
          detalles: `Tipo: ${nueva.configuracion.parameters.type}`,
        },
      ]);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  // Actualizar
  async function editarProgramacion(actualizada) {
    try {
      const resp = await apiActualizarProgramacion(actualizada);
      setMensaje(resp.mensaje || "");
      await fetchData();
      const sectorNames =
        (actualizada.configuracion.parameters.valves || [])
          .map((v) => String(v.valvulaId))
          .join(", ");
      setHistorial((h) => [
        ...h,
        {
          id: Date.now(),
          tipo: "Modificación",
          fecha: new Date().toLocaleString(),
          programacionId: actualizada.programacionId,
          sectores: sectorNames,
          detalles: `Tipo: ${actualizada.configuracion.parameters.type}`,
        },
      ]);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  // Eliminar
  async function borrarProgramacion(prog, sectores) {
    try {
      const resp = await apiEliminarProgramacion(prog.programacionId);
      setMensaje(resp.mensaje || "");
      await fetchData();
      setHistorial((h) => [
        ...h,
        {
          id: Date.now(),
          tipo: "Eliminación",
          fecha: new Date().toLocaleString(),
          programacionId: prog.programacionId,
          sectores: sectores.join(", "),
          detalles: `Tipo: ${prog.configuracion.parameters.type}`,
        },
      ]);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  return {
    programaciones,
    historial,
    ultimoId,
    loading,
    error,
    mensaje,
    agregarProgramacion,
    editarProgramacion,
    borrarProgramacion,
  };
}
